// SolarInstallationFooter.js - Solar Installation themed footer component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a Solar Installation themed footer section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the footer is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer elements
 */
export const createSolarInstallationFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const footerSectionClass = 'solar-footer-section';
  const footerContainerClass = 'solar-footer-container';
  const footerTopClass = 'solar-footer-top';
  const footerColumnsClass = 'solar-footer-columns';
  const footerColumnClass = 'solar-footer-column';
  const footerLogoClass = 'solar-footer-logo';
  const footerDescriptionClass = 'solar-footer-description';
  const footerHeadingClass = 'solar-footer-heading';
  const footerLinksClass = 'solar-footer-links';
  const footerLinkClass = 'solar-footer-link';
  const footerSocialClass = 'solar-footer-social';
  const footerSocialLinkClass = 'solar-footer-social-link';
  const footerBottomClass = 'solar-footer-bottom';
  const footerCopyrightClass = 'solar-footer-copyright';
  const footerContactInfoClass = 'solar-footer-contact-info';
  const footerContactItemClass = 'solar-footer-contact-item';

  // Desktop Styles - Solar themed with deep blue background
  const footerSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '30px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f2341',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0f2341 0%, #1e3a8a 50%, #0f2341 100%)
    `,
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const footerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    gap: '50px'
  };

  const footerTopStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '50px',
    width: '100%'
  };

  const footerColumnsStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '40px'
  };

  const footerColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    minWidth: '200px',
    flex: '1'
  };

  const footerLogoStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#4ade80',
    marginBottom: '20px',
    textDecoration: 'none',
    fontFamily: '"Inter", sans-serif',
    textShadow: '0 0 10px rgba(74, 222, 128, 0.3)'
  };

  const footerDescriptionStyles = {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#e2e8f0',
    maxWidth: '350px',
    fontFamily: '"Inter", sans-serif'
  };

  const footerHeadingStyles = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#4ade80',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif'
  };

  const footerLinksStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };

  const footerLinkStyles = {
    fontSize: '15px',
    color: '#cbd5e1',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif'
  };

  const footerContactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };

  const footerContactItemStyles = {
    fontSize: '15px',
    color: '#cbd5e1',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const footerSocialStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    marginTop: '15px'
  };

  const footerSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
    border: '2px solid rgba(74, 222, 128, 0.3)',
    color: '#4ade80',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)'
  };

  const footerBottomStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '30px',
    borderTop: '1px solid rgba(74, 222, 128, 0.2)',
    flexWrap: 'wrap',
    gap: '20px'
  };

  const footerCopyrightStyles = {
    fontSize: '14px',
    color: '#94a3b8',
    fontFamily: '"Inter", sans-serif'
  };

  // Tablet Styles
  const tabletFooterColumnsStyles = {
    ...footerColumnsStyles,
    gap: '30px'
  };

  // Mobile Styles
  const mobileFooterColumnsStyles = {
    ...footerColumnsStyles,
    flexDirection: 'column',
    gap: '40px'
  };

  const mobileFooterColumnStyles = {
    ...footerColumnStyles,
    minWidth: '100%'
  };

  const mobileFooterBottomStyles = {
    ...footerBottomStyles,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  };

  const mobileFooterLogoStyles = {
    ...footerLogoStyles,
    fontSize: '28px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerSectionClass, footerSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContainerClass, footerContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerTopClass, footerTopStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnsClass, footerColumnsStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnClass, footerColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoClass, footerLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDescriptionClass, footerDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerHeadingClass, footerHeadingStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinksClass, footerLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkClass, footerLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContactInfoClass, footerContactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContactItemClass, footerContactItemStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialClass, footerSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialLinkClass, footerSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerBottomClass, footerBottomStyles);
    newRegistry[DEVICES.DESKTOP].set(footerCopyrightClass, footerCopyrightStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(footerColumnsClass, tabletFooterColumnsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerColumnsClass, mobileFooterColumnsStyles);
    newRegistry[DEVICES.MOBILE].set(footerColumnClass, mobileFooterColumnStyles);
    newRegistry[DEVICES.MOBILE].set(footerBottomClass, mobileFooterBottomStyles);
    newRegistry[DEVICES.MOBILE].set(footerLogoClass, mobileFooterLogoStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerSectionClass,
    footerContainerClass,
    footerTopClass,
    footerColumnsClass,
    footerColumnClass,
    footerLogoClass,
    footerDescriptionClass,
    footerHeadingClass,
    footerLinksClass,
    footerLinkClass,
    footerContactInfoClass,
    footerContactItemClass,
    footerSocialClass,
    footerSocialLinkClass,
    footerBottomClass,
    footerCopyrightClass
  ]));

  // Generate IDs for all elements
  const footerSectionId = Date.now();
  const containerId = footerSectionId + 1;
  const topId = footerSectionId + 2;
  const columnsId = footerSectionId + 3;
  
  // Column 1 - Company Info
  const column1Id = footerSectionId + 4;
  const logoId = footerSectionId + 5;
  const descriptionId = footerSectionId + 6;
  
  // Column 2 - Services
  const column2Id = footerSectionId + 7;
  const heading1Id = footerSectionId + 8;
  const links1Id = footerSectionId + 9;
  const link1Id = footerSectionId + 10;
  const link2Id = footerSectionId + 11;
  const link3Id = footerSectionId + 12;
  const link4Id = footerSectionId + 13;
  const link5Id = footerSectionId + 14;
  
  // Column 3 - Quick Links
  const column3Id = footerSectionId + 15;
  const heading2Id = footerSectionId + 16;
  const links2Id = footerSectionId + 17;
  const link6Id = footerSectionId + 18;
  const link7Id = footerSectionId + 19;
  const link8Id = footerSectionId + 20;
  const link9Id = footerSectionId + 21;
  
  // Column 4 - Contact Info
  const column4Id = footerSectionId + 22;
  const heading3Id = footerSectionId + 23;
  const contactInfoId = footerSectionId + 24;
  const contactItem1Id = footerSectionId + 25;
  const contactItem2Id = footerSectionId + 26;
  const contactItem3Id = footerSectionId + 27;
  const contactItem4Id = footerSectionId + 28;
  const socialLinksId = footerSectionId + 29;
  const social1Id = footerSectionId + 30;
  const social2Id = footerSectionId + 31;
  const social3Id = footerSectionId + 32;
  const social4Id = footerSectionId + 33;
  
  // Social media icons
  const social1IconId = footerSectionId + 34;
  const social2IconId = footerSectionId + 35;
  const social3IconId = footerSectionId + 36;
  const social4IconId = footerSectionId + 37;
  
  // Bottom section
  const bottomId = footerSectionId + 38;
  const copyrightId = footerSectionId + 39;

  const newElements = [
    // Main Footer Section Container
    {
      id: footerSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Solar Footer Section',
      parentId: null,
      className: footerSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-section',
        'data-component': 'section',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Container',
      parentId: footerSectionId,
      hierarchyName: getHierarchyName('div'),
      className: footerContainerClass,
      styles: {},
      children: [topId, bottomId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-container'
      }
    },
    
    // Top Section
    {
      id: topId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Top',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: footerTopClass,
      styles: {},
      children: [columnsId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-top'
      }
    },
    
    // Columns Container
    {
      id: columnsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Columns',
      parentId: topId,
      hierarchyName: getHierarchyName('div'),
      className: footerColumnsClass,
      styles: {},
      children: [column1Id, column2Id, column3Id, column4Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-columns'
      }
    },
    
    // Column 1 - Company Info
    {
      id: column1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Column 1',
      parentId: columnsId,
      hierarchyName: getHierarchyName('div'),
      className: footerColumnClass,
      styles: {},
      children: [logoId, descriptionId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-column'
      }
    },
    
    // Logo
    {
      id: logoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Solar Footer Logo',
      parentId: column1Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLogoClass,
      styles: {},
      children: [],
      text: 'JNT & Co.',
      linkTo: 'index',
      attributes: {
        'data-element': 'solar-footer-logo',
        'href': '#'
      }
    },
    
    // Description
    {
      id: descriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Solar Footer Description',
      parentId: column1Id,
      hierarchyName: getHierarchyName('p'),
      className: footerDescriptionClass,
      styles: {},
      children: [],
      text: 'Leading provider of solar installation, CCTV systems, electrical door locks, and intercom solutions. Powering your future with clean energy and advanced security technology.',
      attributes: {
        'data-element': 'solar-footer-description'
      }
    },
    
    // Column 2 - Services
    {
      id: column2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Column 2',
      parentId: columnsId,
      hierarchyName: getHierarchyName('div'),
      className: footerColumnClass,
      styles: {},
      children: [heading1Id, links1Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-column'
      }
    },
    
    // Services Heading
    {
      id: heading1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Solar Footer Services Heading',
      parentId: column2Id,
      hierarchyName: getHierarchyName('h4'),
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Our Services',
      attributes: {
        'data-element': 'solar-footer-heading'
      }
    },
    
    // Services Links
    {
      id: links1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Services Links',
      parentId: column2Id,
      hierarchyName: getHierarchyName('div'),
      className: footerLinksClass,
      styles: {},
      children: [link1Id, link2Id, link3Id, link4Id, link5Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-links'
      }
    },
    
    // Service Link 1
    {
      id: link1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Solar Installation Link',
      parentId: links1Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Solar System Installation',
      linkTo: 'solar-installation',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Service Link 2
    {
      id: link2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'CCTV Installation Link',
      parentId: links1Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'CCTV Installation & Repair',
      linkTo: 'cctv-service',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Service Link 3
    {
      id: link3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Door Lock Systems Link',
      parentId: links1Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Electrical Door Lock Systems',
      linkTo: 'door-lock-service',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Service Link 4
    {
      id: link4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Intercom Systems Link',
      parentId: links1Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Intercoms & PABX Systems',
      linkTo: 'intercom-service',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Service Link 5
    {
      id: link5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Maintenance Services Link',
      parentId: links1Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Maintenance & Support',
      linkTo: 'maintenance-service',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Column 3 - Quick Links
    {
      id: column3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Column 3',
      parentId: columnsId,
      hierarchyName: getHierarchyName('div'),
      className: footerColumnClass,
      styles: {},
      children: [heading2Id, links2Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-column'
      }
    },
    
    // Quick Links Heading
    {
      id: heading2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Solar Footer Quick Links Heading',
      parentId: column3Id,
      hierarchyName: getHierarchyName('h4'),
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Quick Links',
      attributes: {
        'data-element': 'solar-footer-heading'
      }
    },
    
    // Quick Links
    {
      id: links2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Quick Links',
      parentId: column3Id,
      hierarchyName: getHierarchyName('div'),
      className: footerLinksClass,
      styles: {},
      children: [link6Id, link7Id, link8Id, link9Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-links'
      }
    },
    
    // Quick Link 1
    {
      id: link6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'About Us Link',
      parentId: links2Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'About Us',
      linkTo: 'about',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Quick Link 2
    {
      id: link7Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Projects Link',
      parentId: links2Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Our Projects',
      linkTo: 'projects',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Quick Link 3
    {
      id: link8Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Testimonials Link',
      parentId: links2Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Testimonials',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Quick Link 4
    {
      id: link9Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Get Quote Link',
      parentId: links2Id,
      hierarchyName: getHierarchyName('link'),
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Get Free Quote',
      linkTo: 'get-quote',
      attributes: {
        'data-element': 'solar-footer-link',
        'href': '#'
      }
    },
    
    // Column 4 - Contact Info
    {
      id: column4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Column 4',
      parentId: columnsId,
      hierarchyName: getHierarchyName('div'),
      className: footerColumnClass,
      styles: {},
      children: [heading3Id, contactInfoId, socialLinksId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-column'
      }
    },
    
    // Contact Heading
    {
      id: heading3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Solar Footer Contact Heading',
      parentId: column4Id,
      hierarchyName: getHierarchyName('h4'),
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Contact Info',
      attributes: {
        'data-element': 'solar-footer-heading'
      }
    },
    
    // Contact Info Container
    {
      id: contactInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Contact Info',
      parentId: column4Id,
      hierarchyName: getHierarchyName('div'),
      className: footerContactInfoClass,
      styles: {},
      children: [contactItem1Id, contactItem2Id, contactItem3Id, contactItem4Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-contact-info'
      }
    },
    
    // Contact Item 1 - Phone
    {
      id: contactItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Phone',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('div'),
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '📞 0301-2475122',
      attributes: {
        'data-element': 'solar-footer-contact-item'
      }
    },
    
    // Contact Item 2 - Email
    {
      id: contactItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Email',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('div'),
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '✉️ jasimarman8@gmail.com',
      attributes: {
        'data-element': 'solar-footer-contact-item'
      }
    },
    
    // Contact Item 3 - Address
    {
      id: contactItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Address',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('div'),
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '📍 P-724 Block-N North Nazimabad West Karachi',
      attributes: {
        'data-element': 'solar-footer-contact-item'
      }
    },
    
    // Contact Item 4 - New Address
    {
      id: contactItem4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer New Address',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('div'),
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '📍 Shop # 2 Ayaz Appartment Block# 2 Gulshan-e-Iqbal Karachi',
      attributes: {
        'data-element': 'solar-footer-contact-item'
      }
    },
    
    // Social Links Container
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Social Links',
      parentId: column4Id,
      hierarchyName: getHierarchyName('div'),
      className: footerSocialClass,
      styles: {},
      children: [social1Id, social2Id, social3Id, social4Id],
      text: '',
      attributes: {
        'data-element': 'solar-footer-social'
      }
    },
    
    // Social Link 1 - Facebook
    {
      id: social1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Solar Footer Facebook',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: footerSocialLinkClass,
      styles: {},
      children: [social1IconId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-social-link',
        'href': '#',
        'aria-label': 'Facebook'
      }
    },
    
    // Facebook Icon
    {
      id: social1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: social1Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },
    
    // Social Link 2 - Twitter
    {
      id: social2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Solar Footer Twitter',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: footerSocialLinkClass,
      styles: {},
      children: [social2IconId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-social-link',
        'href': '#',
        'aria-label': 'Twitter'
      }
    },
    
    // Twitter Icon
    {
      id: social2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: social2Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Twitter'
      }
    },
    
    // Social Link 3 - LinkedIn
    {
      id: social3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Solar Footer LinkedIn',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: footerSocialLinkClass,
      styles: {},
      children: [social3IconId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-social-link',
        'href': '#',
        'aria-label': 'LinkedIn'
      }
    },
    
    // LinkedIn Icon
    {
      id: social3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: social3Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'LinkedIn'
      }
    },
    
    // Social Link 4 - Instagram
    {
      id: social4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Solar Footer Instagram',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: footerSocialLinkClass,
      styles: {},
      children: [social4IconId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-social-link',
        'href': '#',
        'aria-label': 'Instagram'
      }
    },
    
    // Instagram Icon
    {
      id: social4IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: social4Id,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },
    
    // Bottom Section
    {
      id: bottomId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Footer Bottom',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: footerBottomClass,
      styles: {},
      children: [copyrightId],
      text: '',
      attributes: {
        'data-element': 'solar-footer-bottom'
      }
    },
    
    // Copyright
    {
      id: copyrightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Solar Footer Copyright',
      parentId: bottomId,
      hierarchyName: getHierarchyName('p'),
      className: footerCopyrightClass,
      styles: {},
      children: [],
      text: `© ${new Date().getFullYear()} JNT & Co. All rights reserved. | Powering your future with clean energy solutions.`,
      attributes: {
        'data-element': 'solar-footer-copyright'
      }
    }
  ];
  
  // Return all elements
  return {
    solarFooterSection: newElements[0],
    elements: newElements
  };
};

export default createSolarInstallationFooterElements; 